import React, { useState, useRef, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  VStack,
  FormControl,
  Input,
  Textarea,
  InputGroup,
  InputLeftElement,
  FormHelperText,
  Icon,
  Button,
  useToast,
  useOutsideClick,
  IconButton,
  Heading,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { FaUser, FaEnvelope, FaComment, FaTimes, FaEnvelopeOpen } from "react-icons/fa";
import axios from "../../services/axios";

const MotionContent = motion(ModalContent);
const CHAR_LIMIT = 500;

/** Contact overlay — dark‑card look, state resets each time it opens */
export default function Contact({ isOpen, onClose }) {
  const toast = useToast();
  const [step, setStep] = useState("form");
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "" });

  /** reset state whenever the modal is opened */
  useEffect(() => {
    if (isOpen) {
      setStep("form");
      setForm({ name: "", email: "", message: "" });
      setSending(false);
    }
  }, [isOpen]);

  const disabled =
    !form.name || !form.email || !form.message || form.message.length > CHAR_LIMIT;

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post("/contact", form);
      setStep("done");
    } catch (err) {
      toast({
        title: "Sending failed",
        description: err.response?.data?.detail ?? "Network error – try again later.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSending(false);
    }
  };

  /* outside click */
  const ref = useRef(null);
  useOutsideClick({ ref, handler: onClose });

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg" motionPreset="scale">
      <ModalOverlay bg="rgba(0,0,0,.6)" />

      <MotionContent
        ref={ref}
        maxW="600px"
        w="90%"
        p={8}
        bg="dark.100"
        borderRadius="lg"
        border="1px solid rgba(255,255,255,0.08)"
        boxShadow="2xl"
        position="relative"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
      >
        {/* custom close × */}
        <IconButton
          icon={<FaTimes />}
          aria-label="Close"
          size="sm"
          position="absolute"
          top={3}
          right={3}
          variant="ghost"
          color="primary.100"
          onClick={onClose}
        />

        {step === "form" && (
          <>
            <Heading
              mb={6}
              textAlign="center"
              textShadow="0 0 5px rgba(207,177,130,.8)"
              color="primary.100"
            >
              Contact&nbsp;Us
            </Heading>

            <ModalBody p={0} bg="transparent">
              <VStack as="form" spacing={6} onSubmit={submit}>
                {/* NAME */}
                <FormControl isRequired>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FaUser} color="gray.400" />
                    </InputLeftElement>
                    <Input
                      name="name"
                      placeholder="Enter your name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      variant="filled"
                      _placeholder={{ color: "secondary.500" }}
                    />
                  </InputGroup>
                </FormControl>

                {/* EMAIL */}
                <FormControl isRequired>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FaEnvelope} color="gray.400" />
                    </InputLeftElement>
                    <Input
                      type="email"
                      name="email"
                      placeholder="Enter your email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      variant="filled"
                      _placeholder={{ color: "secondary.500" }}
                    />
                  </InputGroup>
                </FormControl>

                {/* MESSAGE */}
                <FormControl isRequired>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none" pt="8px">
                      <Icon as={FaComment} color="gray.400" />
                    </InputLeftElement>
                    <Textarea
                      pl="2.5rem"
                      name="message"
                      placeholder="Tell us what’s on your mind…"
                      rows={6}
                      variant="filled"
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      _placeholder={{ color: "secondary.500" }}
                    />
                  </InputGroup>
                  <FormHelperText textAlign="right">
                    {form.message.length}/{CHAR_LIMIT}
                  </FormHelperText>
                </FormControl>

                <Button
                  type="submit"
                  variant="glassy"
                  width="full"
                  isLoading={sending}
                  isDisabled={disabled}
                >
                  Send
                </Button>
              </VStack>
            </ModalBody>
          </>
        )}

        {step === "done" && (
          <ModalBody p={0} bg="transparent">
            <VStack spacing={6}>
              <Icon as={FaEnvelopeOpen} boxSize={12} color="primary.100" />
              <motion.h3
                style={{ color: "var(--chakra-colors-primary-100)", textAlign: "center" }}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                Thank you!<br />Your message has been sent.
              </motion.h3>
              <Button
                variant="brand"
                onClick={onClose}
                _hover={{ bg: "transparent", color: "brand", border: "1px solid", borderColor: "brand" }}
              >
                Close
              </Button>
            </VStack>
          </ModalBody>
        )}
      </MotionContent>
    </Modal>
  );
}
